import { addPositionsToCart, addPositionToCart } from '../actions/cartActions';
import type { AddPositionIntent } from '../state-machine/types';
import { useCartStateSelector } from './useCartStateSelector';

export function useAddArticleToCart(
  articleNumber: string,
  externalArticle: boolean,
  articleDescriptionForErrorMessage: string
) {
  const addingPositionIsScheduled = useCartStateSelector<boolean>(state =>
    state?.context?.positionIntents?.some(
      positionIntent => positionIntent.operation === 'ADD' && positionIntent.articleNumber === articleNumber
    )
  );
  const addingPositionIsInProgress = useCartStateSelector<boolean>(state => {
    const scheduledPositionIntent = state?.context?.scheduledPositionIntent;
    return scheduledPositionIntent?.operation === 'ADD' && scheduledPositionIntent?.articleNumber === articleNumber;
  });
  const isAddingPosition = addingPositionIsScheduled || addingPositionIsInProgress;

  function addArticleToCart(quantity: number) {
    addPositionToCart(articleNumber, quantity, externalArticle, articleDescriptionForErrorMessage);
  }

  return {
    addArticleToCart,
    isAddingPosition
  };
}

export type AddArticleToCartParams = {
  articleNumber: string;
  quantity: number;
  externalArticle: boolean;
  articleDescriptionForErrorMessage: string;
};
export function useAddArticlesToCart(positions: AddArticleToCartParams[]) {
  const isAddingPosition = useCartStateSelector<boolean>(state =>
    state?.matches({ ProcessingPositions: 'ProcessingPositionIntent' })
  );
  const pendingAddPositionIntents = usePendingAddPositionIntents();

  function addArticlesToCart() {
    addPositionsToCart(positions);
  }

  return {
    addArticlesToCart,
    isAddingPosition,
    pendingAddPositionArticleNumbers: pendingAddPositionIntents.map(positionIntent => positionIntent.articleNumber)
  };
}

export function usePendingAddPositionIntents() {
  const pendingAddPositionIntents = useCartStateSelector<AddPositionIntent[]>(state => {
    const pendingAddIntents = state?.context?.positionIntents.filter(
      positionIntent => positionIntent.operation === 'ADD'
    );
    if (state?.context?.scheduledPositionIntent?.operation === 'ADD') {
      pendingAddIntents.push(state.context.scheduledPositionIntent);
    }
    return pendingAddIntents ?? [];
  });
  return pendingAddPositionIntents;
}